import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { oneLine } from 'common-tags';

import ArrowRight from '@/icons/light/arrow-right.svg';

import type { FunctionComponent, KeyboardEvent } from 'react';
import type { TypePostalProps } from './types';

/**
 * Postal con dos caras (Figma HoM). Click/Enter/Space flipea; el CTA de
 * la cara trasera navega (stopPropagation para no re-flipear). El padre
 * (`ComponentRevisadasPostales`) controla rotación y posicionamiento.
 */
export const Postal: FunctionComponent<TypePostalProps> = ({
  className = '',
  name,
  description,
  image,
  href,
  ctaPosition = 'bottom',
  nameFontFamily,
  nameStyleClass,
}: TypePostalProps) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const flip = () => setIsFlipped((v) => !v);
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      flip();
    }
  };

  // Priority: nameFontFamily inline > nameStyleClass (`text-style-{slug}`) >
  // default `font-accent` + clamp.
  // Strapi GraphQL convierte espacios en `_` en enums; revertimos para que
  // el browser encuentre la fuente cargada (si no, cae a `cursive`).
  const resolvedFamily = nameFontFamily?.replace(/_/g, ' ');
  const nameStyle: { fontFamily?: string; fontSize?: string } = {};
  if (resolvedFamily) {
    nameStyle.fontFamily = `"${resolvedFamily}", cursive`;
  }
  if (!nameStyleClass) {
    nameStyle.fontSize = 'clamp(2.5rem, 6vw, 4.5rem)';
  }
  const nameClass = oneLine`leading-none ${nameStyleClass ?? 'font-accent'}`;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isFlipped}
      aria-label={
        isFlipped
          ? `${name}: vista trasera. Pulsa para volver al frente`
          : `${name}: pulsa para ver detalles`
      }
      onClick={flip}
      onKeyDown={handleKeyDown}
      className={oneLine`relative aspect-[3/2] w-[280px] cursor-pointer sm:w-[360px] lg:w-[460px] ${className}`}
      style={{ perspective: '1200px' }}
    >
      <div
        className={oneLine`relative h-full w-full transition-transform duration-700 ease-in-out ${
          isFlipped ? '[transform:rotateY(180deg)]' : ''
        }`}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Front */}
        <div
          aria-hidden={isFlipped}
          className="bg-neutrals-0 absolute inset-0 overflow-hidden p-3 shadow-[0_8px_16px_rgba(0,0,0,0.16)] lg:p-4"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <span className="relative block h-full w-full overflow-hidden">
            <Image
              src={image.src}
              alt={image.alt || name}
              fill
              sizes="(min-width: 1024px) 480px, (min-width: 640px) 360px, 280px"
              className="object-cover"
            />

            <span
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent"
            />

            <span className="text-neutrals-0 absolute right-4 bottom-4 left-4 flex flex-col">
              <span className="font-primary text-base leading-tight">
                Greetings from
              </span>
              <span className={nameClass} style={nameStyle}>
                {name}
              </span>
            </span>
          </span>
        </div>

        {/* Back */}
        <div
          aria-hidden={!isFlipped}
          className="bg-neutrals-0 absolute inset-0 [transform:rotateY(180deg)] overflow-hidden p-4 shadow-[0_8px_16px_rgba(0,0,0,0.16)] lg:p-6"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="flex h-full w-full">
            <div className="border-primary-500 flex max-w-[295px] flex-1 flex-col justify-end gap-2 border-r pr-4 lg:pr-6">
              <span
                className={oneLine`text-text-primary ${nameClass}`}
                style={nameStyle}
              >
                {name}
              </span>
              {description && (
                <p className="text-body text-text-primary line-clamp-4 text-sm">
                  {description}
                </p>
              )}
            </div>

            <div
              // Ancho fijo para que el divisor con la columna izquierda
              // quede en la MISMA posición en todas las postales, da igual
              // cuánto texto tenga el name/description.
              className={oneLine`ml-auto flex w-16 shrink-0 justify-end pl-3 ${
                ctaPosition === 'top' ? 'items-start' : 'items-end'
              }`}
            >
              <Link
                href={href}
                prefetch={false}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Ir a ${name}`}
                className="bg-secondary-500 text-neutrals-0 hover:bg-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-secondary-400)))] inline-flex h-9 w-12 shrink-0 items-center justify-center rounded-full transition-colors"
              >
                <ArrowRight width={20} height={20} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Postal.displayName = 'Postal';
